import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import QuickLinks from "./components/QuickLinks";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import { NewsRow } from "@/components/feature/NewsRow";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  { value: "20", label: "收录影视作品" },
  { value: "10", label: "行业资讯与解析" },
  { value: "03", label: "主题专题合集" },
  { value: "04", label: "层级解析框架" },
];

export default function Home() {
  usePageMeta({
    title: "日本伦理 - 专业影视资讯与剧情解析平台",
    description:
      "日本伦理网站为您提供丰富的日本伦理影视剧情介绍、作品精选及行业动态，帮助用户深入了解相关影视内容和文化背景。",
    keywords:
      "日本伦理,日本伦理影视,日本伦理剧情,日本伦理电影,日本伦理剧集,日本影视文化,伦理影视解析",
  });

  const latestMovies = [...movies]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 8);
  const latestNews = [...news].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);
  const featuredSpecial = specials[0];

  return (
    <>
      <HeroCarousel />
      <QuickLinks />

      <section className="mx-auto max-w-[1440px] px-4 py-14 md:px-12 md:py-20">
        <div className="grid grid-cols-2 gap-6 border-b border-background-800 pb-10 md:grid-cols-4 md:gap-8">
          {stats.map((stat) => (
            <div key={stat.label}>
              <p className="font-label text-3xl leading-none text-foreground-950 md:text-4xl">
                {stat.value}
              </p>
              <p className="mt-2 text-xs tracking-wide text-foreground-500 md:text-sm">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 pb-16 md:px-12 md:pb-24">
        <SectionHeading
          eyebrow="Latest Archive"
          title="最新影视作品"
          description="按更新时间整理的新近收录作品，涵盖家庭伦理、社会伦理与青春伦理等方向。"
          action={{ to: "/category", label: "查看全部 20 部" }}
        />
        <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-5 lg:grid-cols-4">
          {latestMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="border-y border-background-800 bg-background-900">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-stretch lg:grid-cols-2">
          <div className="relative min-h-[260px] overflow-hidden lg:min-h-[380px]">
            <img
              src={featuredSpecial.image}
              alt={`${featuredSpecial.title}专题封面`}
              title={`${featuredSpecial.title} - 日本伦理专题合集`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-background-900/70"></div>
            <span className="absolute left-5 top-5 rounded-full bg-background-950/80 px-3 py-1 font-label text-[11px] uppercase tracking-[0.2em] text-accent-400">
              Featured Special
            </span>
          </div>
          <div className="flex flex-col justify-center px-4 py-10 md:px-12 md:py-14">
            <p className="font-label text-xs uppercase tracking-[0.26em] text-accent-500">
              本期专题
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-foreground-950 md:text-3xl">
              {featuredSpecial.title}
            </h2>
            <p className="mt-1 text-sm text-foreground-500">{featuredSpecial.subtitle}</p>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600">
              {featuredSpecial.intro}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {featuredSpecial.groups.map((group) => (
                <span
                  key={group.title}
                  className="rounded-full border border-background-700 px-3 py-1 text-xs text-foreground-600"
                >
                  {group.title}
                </span>
              ))}
            </div>
            <Link
              to="/special"
              className="mt-7 inline-flex w-fit items-center gap-2 rounded-md border border-accent-500 px-5 py-2.5 text-sm text-accent-400 transition-colors hover:bg-accent-500 hover:text-background-950"
            >
              进入专题合集
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 md:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <SectionHeading
              eyebrow="Industry & Analysis"
              title="热门资讯推荐"
              description="行业动态、剧集观察与文化解析，为观看提供更完整的背景。"
              action={{ to: "/news", label: "全部资讯" }}
            />
            <div className="mt-2">
              {latestNews.map((item) => (
                <NewsRow key={item.id} item={item} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
              <p className="font-label text-xs uppercase tracking-[0.22em] text-accent-500">
                编辑说明
              </p>
              <h3 className="mt-3 text-base font-medium text-foreground-950">
                我们如何撰写剧情解析
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                解析分为剧情事实、人物动机、伦理处境与文化背景四个层次，避免简单道德裁决，尽量呈现多方立场与其代价。
              </p>
              <ul className="mt-5 space-y-3 text-sm text-foreground-600">
                {[
                  "收录标准：以伦理关系为核心议题",
                  "内容边界：不涉及真实作品与人物",
                  "更新节奏：每周新增一篇深度解析",
                ].map((line) => (
                  <li key={line} className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-500" aria-hidden="true"></i>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
              <p className="font-label text-xs uppercase tracking-[0.22em] text-foreground-500">
                快速检索
              </p>
              <h3 className="mt-3 text-base font-medium text-foreground-950">
                找到你要看的那一部
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                支持按题材、年代与评分区间筛选，也可直接搜索片名、导演或演员。
              </p>
              <Link
                to="/category"
                className="mt-5 flex items-center justify-between rounded-md bg-primary-500 px-4 py-3 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
              >
                <span className="whitespace-nowrap">打开作品筛选</span>
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                收藏状态保存在本机浏览器，无需登录即可演示。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-background-800 bg-background-900">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 py-14 md:px-12 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="font-label text-xs uppercase tracking-[0.26em] text-accent-500">
              About This Archive
            </p>
            <h2 className="mt-3 text-2xl font-semibold leading-snug text-foreground-950">
              一座关于伦理叙事的
              <br className="hidden md:block" />
              开放档案室
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-sm leading-relaxed text-foreground-600">
              「日本伦理」是一个专注日本伦理题材影视作品的资料型网站。我们相信，理解一部作品需要的不仅是剧情梗概，还有它所处的社会语境、创作者的选择，以及观众被邀请做出的判断。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              所有内容由编辑团队整理与撰写，作品、人物与机构均为虚构示例，仅用于演示影视资讯与解析的呈现方式。
            </p>
          </div>
          <div className="lg:col-span-3">
            <p className="text-xs text-foreground-500">合作与投稿</p>
            <a
              href="mailto:editor@nihon-rinri-demo.jp"
              className="mt-2 inline-flex items-center gap-2 text-sm text-foreground-800 transition-colors hover:text-accent-400"
            >
              <i className="ri-mail-line" aria-hidden="true"></i>
              editor@nihon-rinri-demo.jp
            </a>
            <Link
              to="/about"
              className="mt-5 flex w-fit items-center gap-2 border-b border-background-600 pb-1 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              了解站点定位
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}